import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import CredentialExpiryAlert from "../../../components/dr/CredentialExpiryAlert";
import NotesInput from "../../../components/dr/NotesInput";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";

// Both workflows on this page write a compliance outcome, so both belong to
// a session that may decide. The wording matches DRApprovalDetail's refusal
// so an oversight session reads the same sentence wherever it meets it.
const AD_CANNOT_DECIDE =
  "Platform Manager sessions can reassign or escalate but do not decide KYC, classification or suitability.";

const FINAL_DECISION_OPTIONS = [
  { value: "APPROVED_WITH_JUSTIFICATION", label: "Approve with justification" },
  { value: "MAINTAINED_REJECTION",        label: "Maintain rejection" },
  { value: "DOCS_REQUESTED",              label: "Request further documentation" },
];

function DRSuitabilityFailure() {
  const { id: investmentId } = useParams();
  const navigate = useNavigate();
  // An oversight (Platform Manager) session reaches this write workflow; it
  // holds no reviewer credential, so the credential banner is not its business.
  const { canDecide } = useRole();

  const [failureForm, setFailureForm] = useState({
    attemptNumber: "1",
    failureReason: "",
    notes: "",
  });
  const [failureSubmitting, setFailureSubmitting] = useState(false);
  const [failureMsg, setFailureMsg] = useState(null);
  const [lastCallScheduleId, setLastCallScheduleId] = useState(null);

  const [outcomeForm, setOutcomeForm] = useState({
    callId: "",
    outcomeNotes: "",
    finalDecision: "",
    decisionNotes: "",
  });
  const [outcomeSubmitting, setOutcomeSubmitting] = useState(false);
  const [outcomeMsg, setOutcomeMsg] = useState(null);
  const [confirmingFailure, setConfirmingFailure] = useState(false);

  async function submitFailure(e) {
    // Same guard as the call outcome below, and for the same reason: logging a
    // failure blocks an investor and queues mail in their name. A disabled
    // button is not a gate.
    if (!canDecide) return;
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setConfirmingFailure(false);
    setFailureSubmitting(true);
    setFailureMsg(null);
    try {
      const body = {
        attemptNumber: Number(failureForm.attemptNumber),
        failureReason: failureForm.failureReason.trim(),
      };
      if (failureForm.notes.trim()) body.notes = failureForm.notes.trim();
      const res = await drApi.suitabilityFail(investmentId, body);
      const data = res?.data || {};
      setFailureMsg({
        tone: "info",
        text: `Failure recorded — escalation: ${data.escalation_type}${
          data.call_schedule_id
            ? ` · call #${data.call_schedule_id} booked`
            : data.email_enqueued
            ? " · email queued"
            : ""
        }`,
      });
      if (data.call_schedule_id) {
        setLastCallScheduleId(data.call_schedule_id);
        setOutcomeForm((f) => ({ ...f, callId: String(data.call_schedule_id) }));
      }
      setFailureForm((f) => ({
        ...f,
        attemptNumber: String(Number(f.attemptNumber) + 1),
        failureReason: "",
        notes: "",
      }));
    } catch (err) {
      setFailureMsg({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Submit failed",
      });
    } finally {
      setFailureSubmitting(false);
    }
  }

  async function submitOutcome(e) {
    e.preventDefault();
    // The form is disabled for an oversight session, but a form submits on
    // Enter from any focused field, so the guard belongs here too and not only
    // on the button.
    if (!canDecide) return;
    setOutcomeSubmitting(true);
    setOutcomeMsg(null);
    try {
      const body = { outcomeNotes: outcomeForm.outcomeNotes.trim() };
      if (outcomeForm.finalDecision) {
        body.finalDecision = outcomeForm.finalDecision;
        body.decisionNotes = outcomeForm.decisionNotes.trim();
      }
      const res = await drApi.callScheduleOutcome(outcomeForm.callId, body);
      const data = res?.data || {};
      setOutcomeMsg({
        tone: "info",
        text: `Call #${data.id} completed${
          data.final_decision ? ` · ${data.final_decision}` : ""
        }${data.failure_updated ? " · linked failure updated" : ""}.`,
      });
      setOutcomeForm({ callId: "", outcomeNotes: "", finalDecision: "", decisionNotes: "" });
    } catch (err) {
      setOutcomeMsg({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Submit failed",
      });
    } finally {
      setOutcomeSubmitting(false);
    }
  }

  const failureReasonValid =
    canDecide && failureForm.failureReason.trim().length >= 3;
  const attemptNum = Number(failureForm.attemptNumber);
  const attemptValid = Number.isFinite(attemptNum) && attemptNum >= 1 && attemptNum <= 255;
  const escalationHint =
    attemptNum === 1
      ? "Attempt 1 — education email will be queued."
      : attemptNum === 2
      ? "Attempt 2 — a 1:1 call_schedules row will be created and booking email queued."
      : attemptNum >= 3
      ? "Attempt 3+ — BLOCKED. No email will be sent. AD override required to unblock the investor."
      : "Enter a valid attempt number (1–255).";

  const outcomeNotesValid = outcomeForm.outcomeNotes.trim().length >= 10;
  const decisionNotesValid =
    !outcomeForm.finalDecision || outcomeForm.decisionNotes.trim().length >= 10;
  const outcomeValid =
    canDecide && !!outcomeForm.callId && outcomeNotesValid && decisionNotesValid;

  const headActions = (
    <Btn variant="ghost" size="sm" onClick={() => navigate("/dr/approvals")}>
      <ChevronLeft /> Back to approvals
    </Btn>
  );

  return (
    <PageShell>
      <PageHead
        title="Suitability failure workflow"
        subtitle={<>Investment <span className="mono">#{investmentId}</span></>}
        actions={headActions}
      />

      {canDecide && <CredentialExpiryAlert />}

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Each failure logs a row in <code>suitability_failures</code> and emits one audit entry. Server side-effects depend on the attempt number.
      </p>

      <section className="card">
        <div className="card__head"><div className="card__title">1 — Log a suitability failure</div></div>
        <div className="card__body">
          {!canDecide && <Alert tone="info">{AD_CANNOT_DECIDE}</Alert>}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canDecide && failureReasonValid && attemptValid && !failureSubmitting) {
                setConfirmingFailure(true);
              }
            }}
            style={{ display: "flex", flexDirection: "column", gap: 12 }}
          >
            <div className="form-grid form-grid--2col">
              <Field
                label="Attempt number *"
                name="attempt_number"
                type="number"
                min="1"
                max="255"
                value={failureForm.attemptNumber}
                onChange={(e) => setFailureForm({ ...failureForm, attemptNumber: e.target.value })}
                hint={escalationHint}
                disabled={!canDecide}
                required
              />
            </div>

            <Field
              as="textarea"
              label="Failure reason *"
              name="failure_reason"
              rows={3}
              maxLength={8192}
              value={failureForm.failureReason}
              onChange={(e) => setFailureForm({ ...failureForm, failureReason: e.target.value })}
              placeholder="Why did suitability fail?"
              disabled={!canDecide}
              required
            />

            <NotesInput
              id="dr-failure-notes"
              label="Additional notes (optional)"
              value={failureForm.notes}
              onChange={(v) => setFailureForm({ ...failureForm, notes: v })}
              required={false}
              minLength={0}
              disabled={!canDecide}
            />

            {failureMsg && <Alert tone={failureMsg.tone}>{failureMsg.text}</Alert>}

            <div>
              <Btn
                variant="primary"
                disabled={!failureReasonValid || !attemptValid || failureSubmitting}
                onClick={() => setConfirmingFailure(true)}
              >
                {failureSubmitting ? "Logging..." : "Log failure"}
              </Btn>
            </div>
          </form>

          <Dialog
            open={confirmingFailure}
            onClose={() => !failureSubmitting && setConfirmingFailure(false)}
            aria-labelledby="dr-suit-confirm-title"
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle id="dr-suit-confirm-title">Log suitability failure?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This writes a row to <code>suitability_failures</code>,{" "}
                {attemptNum === 1
                  ? "queues an education email to the investor,"
                  : attemptNum === 2
                  ? "creates a call_schedules row and queues a booking email,"
                  : "blocks the investor (no email, AD override required to unblock),"}{" "}
                and writes an audit-log entry. The action cannot be undone without an AD override.
              </DialogContentText>
              <p style={{ marginTop: 12 }}>
                <strong>Attempt:</strong> {attemptNum}
                <br />
                <strong>Reason:</strong> {failureForm.failureReason}
              </p>
            </DialogContent>
            <DialogActions>
              <Btn onClick={() => setConfirmingFailure(false)} disabled={failureSubmitting}>Back</Btn>
              <Btn variant="primary" onClick={submitFailure} disabled={failureSubmitting} autoFocus>
                {failureSubmitting ? "Logging..." : "Confirm & log"}
              </Btn>
            </DialogActions>
          </Dialog>
        </div>
      </section>

      <section className="card">
        <div className="card__head"><div className="card__title">2 — Record a call outcome</div></div>
        <div className="card__body">
          <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
            Use this after a 1:1 call has completed. The call ID is returned in step 1 when you log a second-attempt failure
            {lastCallScheduleId ? <> — most recent: <strong className="mono">#{lastCallScheduleId}</strong>.</> : "."}
          </p>
          {!canDecide && <Alert tone="info">{AD_CANNOT_DECIDE}</Alert>}
          <form
            onSubmit={submitOutcome}
            style={{ display: "flex", flexDirection: "column", gap: 12 }}
          >
            <Field
              label="Call ID *"
              name="call_id"
              type="number"
              min="1"
              value={outcomeForm.callId}
              onChange={(e) => setOutcomeForm({ ...outcomeForm, callId: e.target.value })}
              placeholder="e.g. 42"
              disabled={!canDecide}
              required
            />

            <Field
              as="textarea"
              label="Outcome notes * (min 10 chars)"
              name="outcome_notes"
              rows={3}
              maxLength={8192}
              value={outcomeForm.outcomeNotes}
              onChange={(e) => setOutcomeForm({ ...outcomeForm, outcomeNotes: e.target.value })}
              placeholder="What was discussed? What signals came out of the call?"
              disabled={!canDecide}
              required
            />

            <Field
              as="select"
              label="Final decision (optional)"
              name="final_decision"
              value={outcomeForm.finalDecision}
              onChange={(e) => setOutcomeForm({ ...outcomeForm, finalDecision: e.target.value })}
              disabled={!canDecide}
            >
              <option value="">— None (call only) —</option>
              {FINAL_DECISION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Field>

            {outcomeForm.finalDecision && (
              <NotesInput
                id="dr-outcome-decision-notes"
                label="Decision notes * (min 10 chars)"
                value={outcomeForm.decisionNotes}
                onChange={(v) => setOutcomeForm({ ...outcomeForm, decisionNotes: v })}
                disabled={!canDecide}
                required
              />
            )}

            {outcomeMsg && <Alert tone={outcomeMsg.tone}>{outcomeMsg.text}</Alert>}

            <div>
              <Btn variant="primary" type="submit" disabled={!outcomeValid || outcomeSubmitting}>
                {outcomeSubmitting ? "Saving..." : "Record outcome"}
              </Btn>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
}

export default DRSuitabilityFailure;
